import { PropsWithChildren, ReactNode, forwardRef } from 'react';
import { cx } from '@linaria/atomic';
import { theme } from '@frontend/theme';
import { SpinningLoader } from '@frontend/design-system';
import { Body } from './atoms/body';
import { Header, HeaderProps } from './atoms/header';
import { Action } from './atoms/header/action';
import { Breadcrumbs } from './atoms/header/breadcrumbs';
import { Heading } from './atoms/header/heading';
import { LocationChip } from './atoms/header/location-chip';
import { SettingsBreadcrumbs } from './atoms/header/settings-breadcrumbs';
import { SettingsLocationChip } from './atoms/header/settings-location-chip';
import { Subtitle } from './atoms/header/subtitle';
import { Title } from './atoms/header/title';
import { PageStyles } from './page.styles';

type MaxWidthProp = string | number | null;

export type PageCompoundProps = PropsWithChildren<{
  children?: ReactNode;
  action?: ReactNode;
  className?: string;
  id?: string;
  loading?: boolean;
  maxWidth?: MaxWidthProp;
  style?: Record<string, any>;
  bgColor?: string;
}>;

const PageCompound = forwardRef<HTMLDivElement, PageCompoundProps>(
  ({ children, className, id, loading, maxWidth = 1400, bgColor = theme.colors.white, style, ...rest }, ref) => {
    if (loading) {
      return (
        <div
          ref={ref}
          style={{ width: '100%', height: '100%', display: 'flex', justifyContent: 'center', alignItems: 'center' }}
          {...rest}
        >
          <SpinningLoader size='xl' />
        </div>
      );
    }

    const resolvedMaxWidth = maxWidth === null ? undefined : typeof maxWidth === 'number' ? `${maxWidth}px` : maxWidth;

    return (
      <div
        ref={ref}
        className={cx(className, PageStyles.pageStyle)}
        id={id}
        style={{
          backgroundColor: bgColor,
          maxWidth: resolvedMaxWidth ?? '1000px',
          ...style,
        }}
        {...rest}
      >
        {children}
      </div>
    );
  }
);

PageCompound.displayName = 'Page';

interface HeaderNamespace extends React.FC<HeaderProps> {
  Action: typeof Action;
  Breadcrumbs: typeof Breadcrumbs;
  Heading: typeof Heading;
  LocationChip: typeof LocationChip;
  SettingsBreadcrumbs: typeof SettingsBreadcrumbs;
  SettingsLocationChip: typeof SettingsLocationChip;
  Subtitle: typeof Subtitle;
  Title: typeof Title;
}

interface PageNamespace
  extends React.ForwardRefExoticComponent<PageCompoundProps & React.RefAttributes<HTMLDivElement>> {
  Header: HeaderNamespace;
  Body: typeof Body;
}

const HeaderNamespace = Header as HeaderNamespace;

HeaderNamespace.Action = Action;
HeaderNamespace.Breadcrumbs = Breadcrumbs;
HeaderNamespace.Heading = Heading;
HeaderNamespace.LocationChip = LocationChip;
HeaderNamespace.SettingsBreadcrumbs = SettingsBreadcrumbs;
HeaderNamespace.SettingsLocationChip = SettingsLocationChip;
HeaderNamespace.Subtitle = Subtitle;
HeaderNamespace.Title = Title;

const PageNamespace = PageCompound as PageNamespace;

PageNamespace.Header = HeaderNamespace;
PageNamespace.Body = Body;

export { PageNamespace as Page };
